import { useState } from 'react'
import { Alert, Badge, Button, Group, Loader, Table, Text, TextInput, Title } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useClient } from '@/hooks/useClient'
import type { OrpcClient } from '@/orpc/client'
import { formatDateTime } from '@/utils.ts'
import { logError } from '@/utils/errorUtils'
import { Navigation } from './components'

type PersonaPilotData = Awaited<ReturnType<OrpcClient['getPersonaPilot']>>
type FlagState = PersonaPilotData['flag']

const PERSONA_PILOT_QUERY_KEY = ['super', 'persona-pilot'] as const

const FLAG_BADGE: Record<FlagState, { color: string; label: string }> = {
  on: { color: 'green', label: 'Flag on' },
  off: { color: 'gray', label: 'Flag off' },
  unconfigured: { color: 'orange', label: 'LaunchDarkly not set up' },
  unavailable: { color: 'red', label: 'LaunchDarkly unreachable — reads as off' },
  local_on: { color: 'yellow', label: 'Flag on (local override)' },
}

const errorMessage = (error: unknown) =>
  error instanceof Error && error.message ? error.message : 'Something went wrong'

/**
 * Super Admin → Persona pilot (docs/PERSONA_IDV_PLAN.md §9, step 2.2): who is in the identity-
 * verification pilot cohort, who enrolled or removed them and when, and whether each is being
 * offered **Verify now** right now. The `persona-idv-pilot` flag is shown read-only — it is turned on
 * and off in LaunchDarkly. Removal is soft: removed patients stay listed.
 */
export function PersonaPilot() {
  const client = useClient()
  const queryClient = useQueryClient()
  const [patientId, setPatientId] = useState('')
  const [busy, setBusy] = useState<string | null>(null)

  const query = useQuery({
    queryKey: PERSONA_PILOT_QUERY_KEY,
    queryFn: () => client.getPersonaPilot(),
  })
  const data = query.data

  const run = async (key: string, action: () => Promise<{ changed: boolean }>, done: string) => {
    setBusy(key)
    try {
      const { changed } = await action()
      showNotification({ color: changed ? 'green' : 'gray', message: changed ? done : 'No change' })
      await queryClient.invalidateQueries({ queryKey: PERSONA_PILOT_QUERY_KEY })
      return changed
    } catch (error) {
      logError('super', 'Persona pilot change failed', error)
      showNotification({ color: 'red', title: 'Not saved', message: errorMessage(error) })
      return false
    } finally {
      setBusy(null)
    }
  }

  const enroll = async () => {
    const id = patientId.trim()
    if (!id) return
    const changed = await run(
      'enroll',
      () => client.enrollPersonaPilotPatient({ patientId: id }),
      'Patient enrolled',
    )
    if (changed) setPatientId('')
  }

  const remove = (id: string) =>
    run(
      `remove:${id}`,
      () => client.removePersonaPilotPatient({ patientId: id }),
      'Patient removed from the pilot',
    )

  const active = data?.members.filter((m) => m.active).length ?? 0

  return (
    <>
      <Navigation />
      <div className="flex flex-col gap-4 p-6">
        <Group justify="space-between">
          <Title order={2}>Persona pilot</Title>
          {data && (
            <Group gap="xs">
              <Badge color={FLAG_BADGE[data.flag].color} variant="light">
                {FLAG_BADGE[data.flag].label}
              </Badge>
              <Badge color={data.persona.configured ? 'green' : 'orange'} variant="light">
                {data.persona.configured
                  ? `Persona ${data.persona.environment}`
                  : 'Persona not configured'}
              </Badge>
            </Group>
          )}
        </Group>

        <Text size="sm" c="dimmed">
          Patients listed as active are offered automatic identity verification only while the
          <code> persona-idv-pilot </code> flag is on in LaunchDarkly and Persona is configured
          here. Everyone else keeps the manual process. Patients under 18 cannot be enrolled.
        </Text>

        {data && !data.persona.configured && (
          <Alert color="orange" title="Persona is not configured in this environment">
            {data.persona.problem ?? `Set ${data.persona.missing.join(', ')}.`}
          </Alert>
        )}

        <Group align="flex-end">
          <TextInput
            label="Patient id"
            placeholder="Patient resource id"
            value={patientId}
            onChange={(event) => setPatientId(event.currentTarget.value)}
            onKeyDown={(event) => event.key === 'Enter' && void enroll()}
            className="w-96"
          />
          <Button
            onClick={() => void enroll()}
            loading={busy === 'enroll'}
            disabled={!patientId.trim()}
          >
            Enroll
          </Button>
        </Group>

        {query.isLoading && <Loader />}
        {query.error && (
          <Alert color="red" title="Could not load the pilot cohort">
            {errorMessage(query.error)}
          </Alert>
        )}

        {data && (
          <>
            <Text size="sm">
              {active} active · {data.members.length - active} removed
            </Text>
            {data.members.length === 0 ? (
              <Text c="dimmed">Nobody is enrolled yet.</Text>
            ) : (
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Patient</Table.Th>
                    <Table.Th>Enrolled</Table.Th>
                    <Table.Th>Removed</Table.Th>
                    <Table.Th>Identity status</Table.Th>
                    <Table.Th>Offered Persona now</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.members.map((member) => (
                    <Table.Tr key={`${member.patientId}-${member.enrolledAt ?? ''}`}>
                      <Table.Td>
                        <a href={`/patients/${member.patientId}/demographics`}>
                          {member.patientName ?? member.patientId}
                        </a>
                      </Table.Td>
                      <Table.Td>
                        {member.enrolledAt ? formatDateTime(member.enrolledAt) : '—'}
                        {member.enrolledBy && (
                          <Text size="xs" c="dimmed">
                            by {member.enrolledBy}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {member.removedAt ? formatDateTime(member.removedAt) : '—'}
                        {member.removedBy && (
                          <Text size="xs" c="dimmed">
                            by {member.removedBy}
                          </Text>
                        )}
                        {member.removalReason && (
                          <Text size="xs" c="dimmed">
                            {member.removalReason}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>{member.identityStatus ?? '—'}</Table.Td>
                      <Table.Td>{member.offered ? 'Yes' : 'No'}</Table.Td>
                      <Table.Td>
                        {member.active && (
                          <Button
                            size="xs"
                            variant="light"
                            color="red"
                            loading={busy === `remove:${member.patientId}`}
                            onClick={() => void remove(member.patientId)}
                          >
                            Remove
                          </Button>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </>
        )}
      </div>
    </>
  )
}
